import { DQ9Rand } from '@/DQ9Rand'
import { IndexNamePair } from '@/interface/IndexNamePair'
import { InputOutputRange } from '@/interface/InputOutputRange'
import {
  MAP_B1F_ENEMY_RANK_RANGES,
  MAP_BOSS_PROBABILITIES,
  MAP_BOSS_RANGES,
  MAP_FLOOR_COUNT_RANGES,
  MAP_NAME1_LIST,
  MAP_NAME1_RANGES,
  MAP_NAME2_LIST,
  MAP_NAME2_RANGES,
  MAP_NAME3_INDEX_BY_TYPE,
  MAP_NAME3_LIST,
  MAP_NAME3_RANGES,
  MAP_TYPE_PROBABILITIES
} from '../tmapBasicInfo/TMapBasicConstants'
import { TMapBasicData } from '../tmapBasicInfo/TMapBasicData'
import { FloorMapData } from './floorMapInfo/FloorMapData'
import { FloorMapLogic } from './floorMapInfo/FloorMapLogic'
import { TBoxItemLogic } from './tboxInfo/TBoxItemLogic'

/**
 * ドラクエ9 宝の地図基本データを算出するクラス
 * @class TMapBasicLogic
 */
class TMapBasicLogic {
  /**
   * @type {DQ9Rand} ドラクエ9基本乱数インスタンス
   */
  private static _dq9rand: DQ9Rand = new DQ9Rand()

  /**
   * 宝の地図基本データを算出
   * @param {number} mapRank 地図ランク
   * @param {number} mapSeed 地図シード
   * @returns {TMapBasicData} 宝の地図基本データ
   */
  public static calculateBasicData(mapRank: number, mapSeed: number): TMapBasicData {
    const basicData: TMapBasicData = new TMapBasicData()
    basicData.mapRank = mapRank
    basicData.mapSeed = mapSeed
    this._dq9rand.seed = mapSeed
    for (let i = 0; i < 12; i++) {
      this._dq9rand.generateRandom()
    }
    basicData.mapType = this.getMapType()
    basicData.mapFloorCount = this.getMapFloorCount(mapRank)
    basicData.b1fEnemyRank = this.getB1fEnemyRank(mapRank)
    basicData.boss = this.getBoss(mapRank)
    for (let i = 0; i < 12; i++) {
      this._dq9rand.generateRandom()
    }
    basicData.mapName1 = this.getMapName1(basicData.b1fEnemyRank)
    basicData.mapName2 = this.getMapName2(basicData.boss.index)
    basicData.mapName3 = this.getMapName3(basicData.mapFloorCount, basicData.mapType.index)
    basicData.mapLevel = this.getMapLevel(
      basicData.b1fEnemyRank,
      basicData.boss.index,
      basicData.mapFloorCount
    )

    // フロアマップデータ算出
    const floorMapDataList: FloorMapData[] = []
    for (let i = 1; i <= basicData.mapFloorCount; i++) {
      const floorMapData: FloorMapData = FloorMapLogic.calculateFloorMap(mapRank, mapSeed, i)
      floorMapData.enemyRank = basicData.b1fEnemyRank + (((i - 1) / 4) | 0)
      floorMapDataList.push(floorMapData)
    }
    basicData.floorMapDataList = floorMapDataList

    // 宝箱のランクを算出
    TBoxItemLogic.calculateTBoxRank(basicData)

    return basicData
  }

  /**
   * 地形タイプを算出
   * @returns {IndexNamePair} 地形タイプ
   */
  private static getMapType(): IndexNamePair {
    const r = this._dq9rand.generateRandom() % 100
    let sum = 0
    const table = MAP_TYPE_PROBABILITIES
    for (let i = 0; i < table.length; i++) {
      sum += table[i].probability
      if (r < sum) {
        return { ...{ index: i, name: table[i].name } }
      }
    }
    return { ...{ index: 0, name: table[0].name } }
  }

  /**
   * 深さを算出
   * @param {number} mapRank 地図ランク
   * @returns {number} 深さ
   */
  private static getMapFloorCount(mapRank: number): number {
    return this.getOutputValueFromRange(MAP_FLOOR_COUNT_RANGES, mapRank)
  }

  /**
   * B1F敵ランクを算出
   * @param {number} mapRank 地図ランク
   * @returns {number} B1F敵ランク
   */
  private static getB1fEnemyRank(mapRank: number): number {
    return this.getOutputValueFromRange(MAP_B1F_ENEMY_RANK_RANGES, mapRank)
  }

  /**
   * ボスを算出
   * @param {number} mapRank 地図ランク
   * @returns {IndexNamePair} ボス
   */
  private static getBoss(mapRank: number): IndexNamePair {
    const bossRanges = MAP_BOSS_RANGES
    const bossProbabilityList = MAP_BOSS_PROBABILITIES
    for (let i = 0; i < bossRanges.length; i++) {
      if (bossRanges[i].minInput <= mapRank && mapRank <= bossRanges[i].maxInput) {
        let sum = 0
        for (let j = bossRanges[i].minOutput; j <= bossRanges[i].maxOutput; j++) {
          sum += bossProbabilityList[j].probability
        }
        const r = this._dq9rand.generateRandom() % sum
        sum = 0
        for (let k = bossRanges[i].minOutput; k <= bossRanges[i].maxOutput; k++) {
          sum += bossProbabilityList[k].probability
          if (r < sum) {
            return { ...{ index: k, name: bossProbabilityList[k].name } }
          }
        }
        break
      }
    }
    return { ...{ index: 0, name: bossProbabilityList[0].name } }
  }

  /**
   * 地図名1を算出
   * @param {number} b1fEnemyRank B1F敵ランク
   * @returns {IndexNamePair} 地図名1
   */
  private static getMapName1(b1fEnemyRank: number): IndexNamePair {
    const resultIndex = this.getOutputValueFromRange(MAP_NAME1_RANGES, b1fEnemyRank)
    return { ...{ index: resultIndex, name: MAP_NAME1_LIST[resultIndex] } }
  }

  /**
   * 地図名2を算出
   * @param {number} bossIndex ボスの要素番号
   * @returns {IndexNamePair} 地図名2
   */
  private static getMapName2(bossIndex: number): IndexNamePair {
    const resultIndex = this.getOutputValueFromRange(MAP_NAME2_RANGES, bossIndex)
    return { ...{ index: resultIndex, name: MAP_NAME2_LIST[resultIndex] } }
  }

  /**
   * 地図名3を算出
   * @param {number} floorCount 深さ
   * @param {number} mapTypeIndex 地形タイプの要素番号
   * @returns {IndexNamePair} 地図名3
   */
  private static getMapName3(floorCount: number, mapTypeIndex: number): IndexNamePair {
    const indexTable = MAP_NAME3_INDEX_BY_TYPE[mapTypeIndex]
    const resultIndex = this.getOutputValueFromRange(MAP_NAME3_RANGES, floorCount)
    return {
      ...{
        index: indexTable[resultIndex],
        name: MAP_NAME3_LIST[indexTable[resultIndex]]
      }
    }
  }

  /**
   * 地図レベルを算出
   * @param {number} b1fEnemyRank B1F敵ランク
   * @param {number} bossIndex ボスの要素番号
   * @param {number} floorCount 深さ
   * @returns {number} 地図レベル
   */
  private static getMapLevel(b1fEnemyRank: number, bossIndex: number, floorCount: number): number {
    const levelBase = (bossIndex + floorCount + b1fEnemyRank - 3) * 3
    const level = levelBase + (this._dq9rand.generateRandom() % 11) - 5
    return level < 1 ? 1 : level > 99 ? 99 : level
  }

  /**
   * 入力値の範囲から出力値の範囲を決定し、最終的な出力値を返す関数
   * @param {InputOutputRange[]} rangeTable 範囲が定義されたテーブル
   * @param {number} inputValue 入力値
   * @returns {number} 出力値
   */
  private static getOutputValueFromRange(rangeTable: InputOutputRange[], inputValue: number): number {
    for (let i = 0; i < rangeTable.length; i++) {
      if (rangeTable[i].minInput <= inputValue && inputValue <= rangeTable[i].maxInput) {
        return this.getNumFromRange(rangeTable[i].minOutput, rangeTable[i].maxOutput)
      }
    }
    return 0
  }

  /**
   * 最小値から最大値までの範囲から乱数で決定した値を返す
   * @param {number} min 最小値
   * @param {number} max 最大値
   * @returns {number} 決定後の値
   */
  private static getNumFromRange(min: number, max: number): number {
    const candidates = max - min + 1
    if (candidates == 0) {
      return min
    }
    return min + (this._dq9rand.generateRandom() % candidates)
  }
}

export { TMapBasicLogic }
